import { CreditCard } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { fmtMoney } from '../../shared/format'
import type { FinAccount } from '../types'
import { SensitiveIban } from './SensitiveIban'

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-2">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right">{children}</dd>
    </div>
  )
}

/** Hesabın banka / kart bilgileri; IBAN maskeli, kart yalnız son 4 hane. */
export function AccountBankInfo({ account: a }: { account: FinAccount }) {
  const { t } = useTranslation()
  if (a.type === 'cash') return null
  if (a.type === 'credit_card') {
    return (
      <div className="space-y-2 rounded-lg border p-3 text-xs">
        <div className="flex items-center gap-2">
          <CreditCard className="size-4 text-muted-foreground" aria-hidden />
          <span className="font-mono text-sm font-semibold tracking-wider">•••• •••• •••• {a.cardLast4}</span>
          {a.cardBrand ? <span className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase">{t(`finance.cardBrand.${a.cardBrand}`)}</span> : null}
        </div>
        <dl className="space-y-1">
          <Row label={t('finance.accounts.cardHolder')}>{a.cardHolder || '—'}</Row>
          <Row label={t('finance.accounts.cardExpiry')}>{a.cardExpiry || '—'}</Row>
          <Row label={t('finance.accounts.cardLimit')}>{fmtMoney(a.cardLimit)}</Row>
          {a.availableLimit ? (
            <Row label={t('finance.accounts.availableLimit')}>
              <span className="font-semibold">{fmtMoney(a.availableLimit)}</span>
            </Row>
          ) : null}
          {a.statementDay || a.dueDay ? (
            <Row label={t('finance.accounts.cycle')}>{t('finance.accounts.cycleValue', { statement: a.statementDay ?? '—', due: a.dueDay ?? '—' })}</Row>
          ) : null}
        </dl>
      </div>
    )
  }
  return (
    <dl className="space-y-1 rounded-lg border p-3 text-xs">
      {a.branch ? <Row label={t('finance.accounts.branch')}>{a.branch}</Row> : null}
      {a.type === 'pos' && a.merchantId ? <Row label={t('finance.accounts.merchantId')}>{a.merchantId}</Row> : null}
      {a.accountNo ? <Row label={t('finance.accounts.accountNo')}>{a.accountNo}</Row> : null}
      {a.type === 'bank' && Number(a.cashAdvanceLimit) > 0 ? (
        <Row label={t('finance.cashAdvances.accountLimit')}>{fmtMoney(a.cashAdvanceLimit)}</Row>
      ) : null}
      <Row label={t(a.type === 'pos' ? 'finance.accounts.settlementIban' : 'finance.accounts.iban')}>
        <SensitiveIban target="account" id={a.id} masked={a.ibanMasked} />
      </Row>
    </dl>
  )
}
